import * as React from "react";
import { useState } from "react";
import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import EditMapDetails from "./EditMapDetails";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  setSelectedDestinationTableIDMaps,
  setSelectedInterfaceIDMaps,
} from "../../../../../features/mapsSlice";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { MapsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/mapsEndpointLogic";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
const tableHeadings = {
  destination_table_column: "Destination Table Column",
  source_field_expression: "Source Field Expression",
  calculation_type: "Calculation Type ",
  unique_definition: "Unique Definition",
  report_id: "Report Name",
};
const rows = [
  {
    destination_table_column: "activity_date",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
  {
    destination_table_column: "user_unique_id",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
  {
    destination_table_column: "registrations",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
];

const EditMap = () => {
  const [searchParams] = useSearchParams();
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const {
    mapDetails,
    interfaceData,
    destinationTablesData,
    isValidPayloadDetails,
    mapDetailsDataPayload,
  } = useSelector((state: any) => state.maps);
  const mapId: any = searchParams.get("mapId");
  const [loading, setLoading] = useState(true);
  const [selectedInterface, setSelectedInterface] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedDestinationTable, setSelectedDestinationTable] =
    useState(null);
  const [mapNameInput, setMapNameInput] = React.useState("");
  const dispatch = useDispatch();
  const currentParams = window.location.search;
  const navigate = useNavigate();
  const fetchMapsData = async () => {
    setLoading(true);
    try {
      await MapsEndpointLogic.getMapDetails(oauthTokenHeader, mapId);
      await Promise.all([
        MapsEndpointLogic.getInterface(oauthTokenHeader),
        MapsEndpointLogic.getDestinationTables(oauthTokenHeader),
        MapsEndpointLogic.getAllCalculationTypesMaps(oauthTokenHeader),
        MapsEndpointLogic.getUnifierUniqueDefinitionMaps(oauthTokenHeader),
      ]);
    } catch (error: any) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.parsedText ||
            "Error while fetching data. Please try again later.",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };
  React.useEffect(() => {
    fetchMapsData();

    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (!mapDetails?.backend_interface_id) return;
    if (!interfaceData?.length) return;
    const matchedInterface = interfaceData.find(
      (i: any) => i.id === mapDetails.backend_interface_id,
    );
    if (!matchedInterface?.shared_uid) return;
    MapsEndpointLogic.getInterfaceReports(
      oauthTokenHeader,
      matchedInterface.shared_uid,
    );
  }, [mapDetails, interfaceData, oauthTokenHeader]);

  const selectedInterfaceValue =
    selectedInterface &&
    interfaceData?.find(
      (item: any) => item.affiliate_system_interface_name === selectedInterface,
    )
      ? {
          label: selectedInterface,
          id: interfaceData.find(
            (item: any) =>
              item.affiliate_system_interface_name === selectedInterface,
          ).id,
        }
      : null;

  const selectedDestinationTableValue =
    selectedDestinationTable &&
    destinationTablesData?.find(
      (item: any) => item.destination_table === selectedDestinationTable,
    )
      ? {
          label: selectedDestinationTable,
          id: destinationTablesData.find(
            (item: any) => item.destination_table === selectedDestinationTable,
          ).id,
        }
      : null;
  // Prefill interface & destination table from mapDetails on load
  React.useEffect(() => {
    if (mapDetails?.map_name) {
      setMapNameInput(mapDetails.map_name); // optional suffix for duplicate
    }
    if (mapDetails?.backend_interface_id && interfaceData?.length > 0) {
      const matchedInterface = interfaceData.find(
        (item: any) => item.id === mapDetails.backend_interface_id,
      );
      if (matchedInterface) {
        setSelectedInterface(matchedInterface.affiliate_system_interface_name);
        dispatch(setSelectedInterfaceIDMaps(matchedInterface.id));
      }
    }

    if (mapDetails?.destination_table_id && destinationTablesData?.length > 0) {
      const matchedDestTable = destinationTablesData.find(
        (item: any) => item.id === mapDetails.destination_table_id,
      );
      if (matchedDestTable) {
        setSelectedDestinationTable(matchedDestTable.destination_table);
        dispatch(setSelectedDestinationTableIDMaps(matchedDestTable.id));
      }
    }
  }, [mapDetails, interfaceData, destinationTablesData, dispatch]);

  const handleEditMap = async () => {
    setIsEditing(true);
    try {
      // const filteredFieldMapping = mapDetailsDataPayload.filter(
      //   (item: any) => !removedSteps.includes(item.source_field_expression)
      // );
      const payload = {
        map_name: mapNameInput.trim(),
        field_mapping: mapDetailsDataPayload,
      };
      const response: any = await MapsEndpointLogic.editMap(
        oauthTokenHeader,
        mapId,
        payload,
      );
      if (response.status === 200) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Map edited successfully",
            type: "success",
          }),
        );
        navigate(`/data_collection/unifier_maps${currentParams}`, {
          replace: true,
        });
      }
    } catch (error: any) {
      console.error("Error editing map:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.message ||
            "Failed to edit map. Please check the details and try again.",
          type: "error",
        }),
      );
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <>
      {isEditing && <SimpleBackdrop />}
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Paper elevation={0} sx={{ padding: "12px 16px", mb: "16px" }}>
            <Grid container alignItems="center" columnSpacing={3}>
              <Grid size={12} mb={2}>
                <Typography variant="h3" sx={{ fontSize: "24px" }}>
                  Edit Map
                </Typography>
              </Grid>
              <Grid size={9}>
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  justifyContent={"flex-end"}
                  gap={2}
                >
                  <TextField
                    required
                    label="Map Name" // Set your label here
                    placeholder="Please enter map name" // Set your placeholder here
                    color="secondary"
                    size="small"
                    fullWidth
                    value={mapNameInput}
                    onChange={(e) => setMapNameInput(e.target.value)}
                    onBlur={() =>
                      setMapNameInput((prev) => (prev ? prev.trim() : prev))
                    }
                  />
                  <Autocomplete
                    sx={{ width: "100%" }}
                    value={selectedInterfaceValue}
                    disabled
                    options={
                      interfaceData?.map((item: any) => ({
                        label: item.affiliate_system_interface_name,
                        id: item.id,
                      })) || []
                    }
                    onChange={(_event, newValue: any) => {
                      setSelectedInterface(newValue?.label);
                      dispatch(setSelectedInterfaceIDMaps(newValue?.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose from the list"
                        label="Interface"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />

                  <Autocomplete
                    sx={{ width: "100%" }}
                    value={selectedDestinationTableValue}
                    disabled
                    options={
                      destinationTablesData?.map((item: any) => ({
                        label: item.destination_table,
                        id: item.id,
                      })) || []
                    }
                    onChange={(_event, newValue: any) => {
                      setSelectedDestinationTable(newValue?.label);
                      dispatch(setSelectedDestinationTableIDMaps(newValue?.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose from the list"
                        label="Destination Table"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />
                </Stack>
              </Grid>
              <Grid size={3} textAlign={"right"}>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    !mapNameInput ||
                    // !selectedInterfaceValue ||
                    // !selectedDestinationTableValue ||
                    !isValidPayloadDetails
                  }
                  onClick={handleEditMap}
                  sx={{
                    padding: "8px 20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Save Changes
                  {/* <GradingIcon sx={{ width: "20px", ml: "5px" }} /> */}
                  {/* {isEditing && <SimpleBackdrop />} */}
                  <SaveOutlinedIcon sx={{ width: "20px", ml: "5px" }} />
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Edit Map Details Table*/}
          <EditMapDetails
            tableHeadings={tableHeadings}
            rows={rows}
            selectedInterface={selectedInterface}
            selectedDestinationTable={selectedDestinationTable}
          />

          {/* Edit Testing Process Modal*/}
          {/* <EditTestingProcessModal
            open={openModal}
            handleClose={handleCloseModal}
            mapNameInput={mapNameInput}
            setMapNameInput={setMapNameInput}
          /> */}
        </>
      )}
    </>
  );
};

export default EditMap;
